"use client";

import { cn, isUUID, shadow } from "@/lib/utils";
import { IAccountAssets, IBlocks, IForm, ITheme, IUserData } from "@/types";
import { BlockUpdateFunction } from ".";
import AdminPopover from "../../content/AdminPopover";
import React from "react";
import BlockToggle from "./tabs/toggle";
import { Separator } from "@/components/ui/separator";
import BlockMetadata from "./tabs/metadata";
import FormOptions from "./tabs/form/options";
import FormRender from "../../forms/Render";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import FormCustomization from "./tabs/form/customization";
import { Skeleton } from "@/components/ui/skeleton";
import DorasIcons from "@/lib/DorasIcons";
import * as tabler from "@tabler/icons-react";
import IconAssetPicker from "./tabs/iconAssetPicker";
import RenderIconOrAsset from "../RenderIconOrAsset";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetOverlay,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    assets: IAccountAssets[];
    theme: ITheme;
    newBlock?: string | null;
    impersonate?: IUserData | null;
}
export default function BlockForm({
    admin,
    user,
    block,
    update,
    style,
    assets,
    theme,
    newBlock,
    impersonate,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [data, setData] = React.useState<IForm | null>(null);
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(
                    `/api/account/${user.id}/form/${blockUpdate.options?.form_id}`,
                    { cache: "no-store" }
                );
                const data = await response.json();
                setData(data.form);
            } catch (error) {
                console.error(error);
                setData(null);
            }
        };
        fetchData();
    }, [user, blockUpdate.options?.form_id]);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Form",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <FormOptions
                        key={block.id}
                        account={user}
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <IconAssetPicker
                        account={user}
                        block={block}
                        update={handleUpdate}
                        assets={assets}
                    />
                    <FormCustomization
                        block={blockUpdate}
                        update={handleUpdate}
                    />
                    <Separator />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Form"}
            isOpen={isExpanded}
            onClose={handleClose}
            key={block.id}
        >
            <div key={block.id} className={`w-full`} id="Form">
                {data ? (
                    <Sheet>
                        <SheetTrigger asChild>
                            <button
                                style={style}
                                id="block"
                                className={`{formatClassName(text)} 
                                        ${shadow(theme.button_shadow)}
                                        ${theme?.button_rounded}
                                        w-full 
                                        px-1 
                                        py-3 text-xl
                                         transition-all 
                                        duration-150 hover:scale-105
                                        ${block.name.replaceAll(" ", "-")}
                                        `}
                            >
                                <RenderIconOrAsset
                                    account_id={user.id}
                                    iconName={blockUpdate.icon}
                                    size={26}
                                />
                                <span
                                    id="block-text"
                                    className={`block w-full px-6 text-center font-bold`}
                                >
                                    {blockUpdate.name.length > 0
                                        ? blockUpdate.name
                                        : data.name}
                                </span>
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            // className=" h-full w-full overflow-hidden border-form-primary-color bg-form-background-color  sm:h-fit sm:max-h-[80dvh]"
                            className="w-full max-w-full p-0 md:w-1/2 md:max-w-full md:border-l md:border-form-primary-color"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                            <SheetHeader className="sr-only">
                                <SheetTitle className="sr-only"></SheetTitle>
                            </SheetHeader>
                            <div className="h-full overflow-auto p-3">
                                <FormRender account={user} form={data} />
                            </div>
                        </SheetContent>
                    </Sheet>
                ) : (
                    <Skeleton className="h-10 w-full rounded-md" />
                )}
            </div>
        </AdminPopover>
    );
}
